import { colors, icons, intl, UUID } from '@beelday/common'
import styled from '@emotion/styled'
import { useEffect, useRef } from 'react'
import { CenteredHint, Eyebrow, Screen, SendError, Tick } from './primitives'

// The question rows are also how Podsumowanie (session-planning) shows a
// participant's own answers on a phone.
export const Item = styled.div<{ $active: boolean }>`
	margin-bottom: 12px;
	overflow: hidden;
	border: 2px solid
		${props => (props.$active ? colors.indigoBlue : colors.borderLightGray)};
	border-radius: 16px;
	background: ${colors.white};
`

export const Header = styled.button`
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	min-height: 52px;
	padding: 12px 16px;
	border: none;
	background: none;
	font-family: inherit;
	text-align: left;
	cursor: pointer;

	&:disabled {
		cursor: default;
	}
`

type BadgeState = 'idle' | 'active' | 'done'

const BADGE_BORDER: Record<BadgeState, string> = {
	idle: colors.borderLightGray,
	active: colors.indigoBlue,
	done: colors.greenishCyan,
}

const BADGE_BACKGROUND: Record<BadgeState, string> = {
	idle: 'transparent',
	active: colors.indigoBlue05,
	done: colors.greenishCyan,
}

export const Badge = styled.span<{ $state: BadgeState }>`
	width: 28px;
	height: 28px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	border: 2px solid ${props => BADGE_BORDER[props.$state]};
	background: ${props => BADGE_BACKGROUND[props.$state]};
	color: ${props =>
		props.$state === 'active' ? colors.indigoBlue : colors.heather};
	font-size: 12px;
	font-weight: 700;
`

export const TextColumn = styled.span`
	flex: 1;
	min-width: 0;
`

export const QuestionText = styled.span`
	display: block;
	font-size: 14px;
	font-weight: 500;
	line-height: 1.4;
	color: ${colors.blackish};
	overflow-wrap: anywhere;
`

const Preview = styled.span`
	display: block;
	margin-top: 2px;
	font-size: 12px;
	font-style: italic;
	color: ${colors.heather};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const Chevron = styled.span<{ $open: boolean }>`
	width: 12px;
	height: 12px;
	flex-shrink: 0;
	display: flex;
	color: ${colors.heather};
	transition: transform 0.2s ease;
	transform: rotate(${props => (props.$open ? 180 : 0)}deg);

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`

const Previews = styled.span`
	display: block;
	margin-top: 2px;
`

/** Every question answered: said once, under the list, instead of the hint. */
const AllSent = styled.p`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin: 16px 0 0;
	font-size: 14px;
	font-weight: 500;
	line-height: 1.4;
	color: ${colors.blackish};
	text-align: center;
`

const AllSentMark = styled.span`
	width: 20px;
	height: 20px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	background: ${colors.greenishCyan};
`

const Body = styled.div`
	padding: 16px;
	border-top: 1px solid ${colors.borderLightGray};
`

const InputRow = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 12px;
`

const Textarea = styled.textarea`
	flex: 1;
	min-width: 0;
	min-height: 88px;
	max-height: 160px;
	padding: 12px 16px;
	border: 2px solid ${colors.borderLightGray};
	border-radius: 8px;
	font-family: inherit;
	/* 16px keeps iOS Safari from zooming the page on focus. */
	font-size: 16px;
	color: ${colors.blackish};
	background: ${colors.white};
	outline: none;
	resize: none;

	&:focus {
		border-color: ${colors.indigoBlue};
	}

	&::placeholder {
		color: ${colors.heather};
	}
`

const SendButton = styled.button`
	width: 48px;
	height: 48px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
	border-radius: 50%;
	background: ${colors.greenishCyan};
	cursor: pointer;

	&:disabled {
		background: ${colors.inactiveGray};
		cursor: not-allowed;
	}
`

export type OpenQuestion = {
	id: UUID
	text: string
	/** Everything I sent to this question, oldest first. */
	myAnswers: string[]
}

type Props = {
	questions: OpenQuestion[]
	/** Answering again is allowed; answered questions stay openable. */
	multiple: boolean
	/** Every question has at least one of my answers. */
	allAnswered: boolean
	openId?: UUID
	drafts: Record<UUID, string>
	sendingId?: UUID
	failedId?: UUID
	/** A send is in flight; one answer goes at a time, so every send waits. */
	busy: boolean
	/** Move focus into the open question - only after the user acted. */
	focusOpen: boolean
	onToggle: (id: UUID) => void
	onDraft: (id: UUID, value: string) => void
	onSend: (id: UUID) => void
}

/**
 * Up to three open questions as an accordion: one open at a time, answered
 * in any order. Desktop lays them side by side as chats; at 390px that is
 * three 110px columns.
 */
export const OpenQuestionsView = ({
	questions,
	multiple,
	allAnswered,
	openId,
	drafts,
	sendingId,
	failedId,
	busy,
	focusOpen,
	onToggle,
	onDraft,
	onSend,
}: Props): JSX.Element => {
	const translate = intl.useTranslate()
	const textareaRef = useRef<HTMLTextAreaElement>(null)

	useEffect(() => {
		if (focusOpen) {
			textareaRef.current?.focus()
		}
	}, [focusOpen, openId])

	return (
		<Screen data-testid="in_person.mobile.poll.open_questions">
			<Eyebrow>
				<intl.Translate>in_person.mobile.poll.open_questions</intl.Translate>
			</Eyebrow>
			{questions.map((question, index) => {
				const done = question.myAnswers.length > 0
				const canOpen = !done || multiple
				const open = openId === question.id && canOpen
				const draft = drafts[question.id] ?? ''
				const sending = sendingId === question.id
				const bodyId = `in-person-open-question-${question.id}`

				return (
					<Item key={question.id} $active={open}>
						<Header
							type="button"
							aria-expanded={open}
							aria-controls={bodyId}
							disabled={!canOpen}
							onClick={() => onToggle(question.id)}
						>
							<Badge $state={done ? 'done' : open ? 'active' : 'idle'}>
								{done ? <Tick /> : index + 1}
							</Badge>
							<TextColumn>
								<QuestionText>{question.text}</QuestionText>
								{done ? (
									<Previews>
										{question.myAnswers.map((answer, answerIndex) => (
											<Preview key={answerIndex}>
												{translate('in_person.mobile.poll.your_answer', {
													answer,
												})}
											</Preview>
										))}
									</Previews>
								) : null}
							</TextColumn>
							{canOpen ? (
								<Chevron $open={open} aria-hidden>
									<icons.ChevronDown />
								</Chevron>
							) : null}
						</Header>
						{open ? (
							<Body id={bodyId}>
								<InputRow>
									<Textarea
										ref={textareaRef}
										value={draft}
										aria-label={question.text}
										placeholder={translate(
											done
												? 'in_person.mobile.poll.open_questions.add_placeholder'
												: 'in_person.mobile.poll.open_questions.placeholder'
										)}
										readOnly={sending}
										onChange={event => onDraft(question.id, event.target.value)}
									/>
									<SendButton
										type="button"
										aria-label={translate('in_person.mobile.send')}
										aria-busy={sending}
										disabled={!draft.trim() || busy}
										onClick={() => onSend(question.id)}
									>
										<icons.SendIcon color={colors.white} />
									</SendButton>
								</InputRow>
								{failedId === question.id ? <SendError /> : null}
							</Body>
						) : null}
					</Item>
				)
			})}
			{allAnswered ? (
				<AllSent role="status">
					<AllSentMark>
						<Tick size={10} />
					</AllSentMark>
					<intl.Translate>
						in_person.mobile.poll.open_questions.all_sent
					</intl.Translate>
				</AllSent>
			) : (
				<CenteredHint>
					<intl.Translate>
						in_person.mobile.poll.open_questions.hint
					</intl.Translate>
				</CenteredHint>
			)}
		</Screen>
	)
}

export default OpenQuestionsView
